import { useState, React } from 'react';
import { Dialog, DialogContent, Button, Box, Typography, IconButton, LinearProgress, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useReservationForm } from '@/hooks/useReservationForm';
import StepPartySize from '@/components/VarausModal/steps/StepPartySize';
import StepSelectDates from '@/components/VarausModal/steps/StepSelectDates';
import StepCustomerType from '@/components/VarausModal/steps/StepCustomerType';
import StepCustomerDetails from '@/components/VarausModal/steps/StepCustomerDetails';
import StepConfirmation from '@/components/VarausModal/steps/StepConfirmation';
import CloseConfirmDialog from '@/components/VarausModal/components/CloseConfirmDialog';
import { useToastError } from '@/hooks/useToastError';
import { toast } from 'react-toastify';
import { createReservation } from '@/services/varausService';
import { useTranslation } from 'react-i18next';

const VarausModal = ({ open, onClose, productId, selectedDate, productName, onReservationCreated }) => {
  const [confirmClose, setConfirmClose] = useState(false);
  const form = useReservationForm({ productId, selectedDate });
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  useToastError(form.constraintError, t('varausModal.fetchError'));

  const steps = [
    t('varausModal.personAmount'),
    t('varausModal.length'),
    t('varausModal.customer'),
    t('varausModal.customerInfo'),
    t('varausModal.confirmation'),
  ];

  // Validation for each step
  const isStepValid = () => {
    switch (form.activeStep) {
      case 0: // Henkilömäärä
        return !!form.partySize;
      case 1: // Päivämäärät
        return !!form.arrivalDate && !!form.departureDate;
      case 2: // "Asiakastyypin" valinta
        return true;
      case 3: // Asiakastiedot
        return true;
      case 4: // Vahvistus
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!isStepValid()) return;

    if (form.activeStep === 0) {
      form.handleFetchAvailableReservations(form.partySize);
    }

    if (form.activeStep === 2 && form.customerChoice === 'none') {
      // Skipataan customerDetails, nollataan asiakkaan valinta jos aiemmin tehty
      form.resetCustomerForm();
      form.setActiveStep(prev => prev + 1);
    }

    if (form.activeStep === 3) {
      if (!form.validateAllRequired()) {
        return;
      }
    }

    form.handleNext();
  };

  const handleBack = () => {
    if (form.submissionResult) {
      form.setSubmissionResult(null);
      return;
    }

    if (form.activeStep === 4 && form.customerChoice === 'none') {
      form.setActiveStep(prev => prev - 2);
    }
    else {
      form.handleBack();
    }
  };

  const handleCloseModal = () => {
    if (form.submissionResult) {
      confirmAndClose();
      return;
    }

    if (form.activeStep > 0) {
      setConfirmClose(true);
    }
    else {
      onClose();
    }
  };

  const confirmAndClose = () => {
    setConfirmClose(false);
    form.resetForm();
    onClose();
  };

  const handleSubmitReservation = async () => {
    setSubmitting(true);
    try {
      const reservationData = form.prepareReservationData();
      const res = await createReservation(reservationData);

      form.setSubmissionResult({
        koriId: res.basketId,
        reservationData: reservationData,
      });

      if (onReservationCreated) {
        onReservationCreated();
      }
    }
    catch (error) {
      form.setSubmissionError(error);
      toast.error(t('varausModal.createError'));
    }
    finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {form.submissionResult ? t('varausModal.created') : t('varausModal.create')}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ overflow: 'visible' }}>
          {!form.submissionResult && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {steps[form.activeStep]}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={((form.activeStep + 1) / steps.length) * 100}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Step content */}
          {form.activeStep === 0 && (
            <StepPartySize
              partySize={form.partySize}
              setPartySize={form.setPartySize}
              minPersons={form.minPersons}
              maxPersons={form.maxPersons}
              loadingConstraints={form.loadingConstraints}
              constraintError={form.constraintError}
            />
          )}
          {form.activeStep === 1 && (
            <StepSelectDates
              partySize={form.partySize}
              arrivalDate={form.arrivalDate}
              setArrivalDate={form.setArrivalDate}
              departureDate={form.departureDate}
              setDepartureDate={form.setDepartureDate}
              availabilityData={form.availabilityData}
              loadingAvailability={form.loadingAvailability}
              inputFormatToDate={form.inputFormatToDate}
              dateToInputFormat={form.dateToInputFormat}
            />
          )}
          {form.activeStep === 2 && (
            <StepCustomerType
              customerChoice={form.customerChoice}
              setCustomerChoice={form.setCustomerChoice}
            />
          )}
          {form.activeStep === 3 && (
            <StepCustomerDetails
              customerChoice={form.customerChoice}
              searchInput={form.searchInput}
              setSearchInput={form.setSearchInput}
              searchResults={form.searchResults}
              searchLoading={form.searchLoading}
              selectedCustomer={form.selectedCustomer}
              customerForm={form.customerForm}
              onSelectSearchResult={form.handleSelectSearchResult}
              onFormChange={form.handleCustomerFormChange}
              onFieldBlur={form.handleFieldBlur}
              fieldErrors={form.fieldErrors}
            />
          )}
          {form.activeStep === 4 && (
            <StepConfirmation
              partySize={form.partySize}
              arrivalDate={form.arrivalDate}
              departureDate={form.departureDate}
              totalPrice={form.totalPrice}
              customerForm={form.customerForm}
              expandedConfirmation={form.expandedConfirmation}
              setExpandedConfirmation={form.setExpandedConfirmation}
              submissionResult={form.submissionResult}
              productName={productName}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              mt: 4,
            }}
          >
            {form.submissionResult
              ? (
                  <>
                    <Button
                      onClick={handleCloseModal}
                      variant="contained"
                      color="primary"
                      sx={{
                        flex: 1,
                      }}
                    >
                      {t('varausModal.close')}
                    </Button>
                  </>
                )
              : (
                  <>
                    <Button
                      disabled={form.activeStep === 0}
                      onClick={handleBack}
                      variant="outlined"
                    >
                      {t('varausModal.prev')}
                    </Button>
                    {form.activeStep === steps.length - 1
                      ? (
                          <Button
                            onClick={handleSubmitReservation}
                            disabled={submitting}
                            variant="contained"
                            color="primary"
                            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                          >
                            {submitting ? t('varausModal.creating') : t('varausModal.confirm')}
                          </Button>
                        )
                      : (
                          <Button
                            onClick={handleNext}
                            variant="contained"
                            color="primary"
                            disabled={!isStepValid()}
                          >
                            {t('varausModal.next')}
                          </Button>
                        )}
                  </>
                )}
          </Box>
        </DialogContent>
      </Dialog>

      <CloseConfirmDialog
        open={confirmClose}
        onKeepEditing={() => setConfirmClose(false)}
        onConfirmClose={confirmAndClose}
      />
    </>
  );
};

export default VarausModal;
